import React from 'react';
import { Popover } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DEFAULT_BATCH, RETURN_REDISPATCH_BATCH } from '../graphql/batchPush';
import { displayNodeLabelKey, formatColumnTime } from '../batchPushUtils';

interface BatchColumnLabelProps {
    column: {
        wmsBatchNumber: string | null;
        displayNode: string | null;
        displayTime: string | null;
    };
    /** 列名主文字字重，默认 font-medium；已选明细 / 确认弹框的批次列传 font-semibold 加粗 */
    titleClassName?: string;
    /** 默认批次列名后加「?」说明（未绑定过批次的包裹），只在批次推送矩阵列头展示 */
    showDefaultTip?: boolean;
}

/** 阻止冒泡：列头点击会整列勾选，「?」及其浮层（React 事件经 Portal 冒泡）不应触发 */
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * 批次列名：回仓重派只显示名称；默认批次显示「时间 默认批次」；其他批次显示「11/1 15:22 hub发运」+ 批次号。
 * 看板列头、已选明细、确认弹框、推送历史共用。
 */
const BatchColumnLabel: React.FC<BatchColumnLabelProps> = ({ column, titleClassName = 'font-medium', showDefaultTip = false }) => {
    const { t } = useTranslation();
    if (column.wmsBatchNumber === RETURN_REDISPATCH_BATCH) {
        return <div className={titleClassName}>{t('wms.batchPush.node.returnRedispatch')}</div>;
    }
    const timeText = formatColumnTime(column.displayTime) ?? '';
    const isDefault = column.wmsBatchNumber === DEFAULT_BATCH;
    const title = `${timeText} ${t(displayNodeLabelKey(isDefault ? 'DEFAULT' : column.displayNode))}`;
    return (
        <div className="min-w-0">
            {/* 列宽不够时省略显示，鼠标悬停展示全文（如英文「batch created」较长） */}
            <div className="flex items-center min-w-0">
                <div className={`${titleClassName} truncate`} title={title}>
                    {title}
                </div>
                {isDefault && showDefaultTip && (
                    <Popover
                        trigger="click"
                        content={<div className="max-w-xs" onClick={stop}>{t('wms.batchPush.columns.defaultBatchTip')}</div>}
                    >
                        <QuestionCircleOutlined
                            className="ml-1 shrink-0 text-gray-400 cursor-pointer"
                            onClick={stop}
                            data-testid="default-batch-tip"
                        />
                    </Popover>
                )}
            </div>
            {!isDefault && (
                <div className="text-xs text-gray-400 truncate" title={column.wmsBatchNumber ?? ''}>
                    {column.wmsBatchNumber}
                </div>
            )}
        </div>
    );
};

export default BatchColumnLabel;
